// src/components/delivery/DeliveryOrderDetails.jsx
import React, { useState } from "react";
import { X, Phone, MapPin, Package, Calendar, User, Mail, ArrowRight, Lock, CheckCircle } from "react-feather";
import { getStatusColor, getStatusLabel, getNextStatus, canUpdateStatus, formatDateTime } from "../../utils/deliveryHelpers";
import config from "../../config";

const DeliveryOrderDetails = ({ order, onClose, onUpdateStatus, onVerifyOtp }) => {
  const [updating, setUpdating] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");

  const handleStatusUpdate = async () => {
    const nextStatus = getNextStatus(order.deliveryStatus);
    if (!nextStatus) return;

    // If moving to BOOK_WITH_ME (Delivered), show OTP modal
    if (nextStatus === 'BOOK_WITH_ME') {
      setShowOtpModal(true);
      return;
    }

    const confirmed = window.confirm(
      `Update order status to ${getStatusLabel(nextStatus)}?`
    );
    if (!confirmed) return;

    setUpdating(true);
    try {
      await onUpdateStatus(order.orderId, nextStatus, order.deliveryStatus);
      onClose();
    } finally {
      setUpdating(false);
    }
  };

  const handleOtpSubmit = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError("Please enter a valid 6-digit OTP");
      return;
    }

    setUpdating(true);
    setOtpError("");
    
    try {
      await onVerifyOtp(order.orderId, otp);
      setShowOtpModal(false);
      onClose();
    } catch (error) {
      setOtpError(error || "Invalid or expired OTP. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtp(value);
    setOtpError("");
  };

  const handleCallCustomer = () => {
    window.location.href = `tel:${order.customerPhone}`;
  };

  const handleOpenMaps = () => {
    const address = encodeURIComponent(
      `${order.deliveryAddress}, ${order.city}, ${order.pincode}`
    );
    window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, "_blank");
  };

  const nextStatus = getNextStatus(order.deliveryStatus);
  const canUpdate = canUpdateStatus(order.deliveryStatus);

  return (
    <>
      {/* OTP Verification Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-slideUp">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Lock size={24} className="text-primary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Verify Delivery OTP</h3>
              </div>
              <button
                onClick={() => {
                  setShowOtpModal(false);
                  setOtp("");
                  setOtpError("");
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                Please ask the customer for the 6-digit OTP sent to their email to confirm delivery.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter OTP
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="6"
                  value={otp}
                  onChange={handleOtpChange}
                  placeholder="000000"
                  className="w-full px-4 py-3 text-center text-2xl font-bold tracking-widest border-2 border-gray-300 rounded-xl focus:border-primary focus:ring-2 focus:ring-orange-200 outline-none transition"
                  autoFocus
                />
              </div>

              {otpError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{otpError}</p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowOtpModal(false);
                  setOtp("");
                  setOtpError("");
                }}
                disabled={updating}
                className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleOtpSubmit}
                disabled={updating || otp.length !== 6}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-primary to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 transition shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {updating ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    <span>Verifying...</span>
                  </div>
                ) : (
                  "Verify & Deliver"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Order Details Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto scrollbar-hide animate-slideUp">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-primary to-orange-600 px-6 py-5 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-xl font-bold text-white">Order Details</h2>
            <p className="text-orange-100 text-sm mt-0.5">Order #{order.orderId}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition text-white"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Book Info */}
          <div className="flex gap-4 bg-gradient-to-br from-gray-50 to-white p-4 rounded-xl border border-gray-100">
            {order.bookCoverImage && (
              <img
                src={
                  order.bookCoverImage && order.bookCoverImage !== "defaultImage.jpg"
                    ? `${config.url}${order.bookCoverImage}`
                    : "/images/defaultBook.png"
                }
                alt={order.bookName}
                className="w-24 h-32 object-cover rounded-lg shadow-md"
                onError={(e) => {
                  e.target.src = "/images/defaultBook.png";
                }}
              />
            )}
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-1">
                {order.bookName}
              </h3>
              <p className="text-gray-600 mb-3 text-sm">{order.bookAuthor}</p>
              <span
                className={`inline-block px-4 py-1.5 rounded-full text-xs font-semibold shadow-sm ${getStatusColor(
                  order.deliveryStatus
                )}`}
              >
                {getStatusLabel(order.deliveryStatus)}
              </span>
            </div>
          </div>

          {/* Order Info */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 space-y-3 border border-blue-100">
            <h4 className="font-bold text-gray-900 mb-3 flex items-center">
              <Package size={18} className="mr-2 text-blue-600" />
              Order Information
            </h4>
            
            <div className="flex items-center text-sm">
              <Package size={18} className="mr-3 text-gray-400" />
              <div>
                <span className="text-gray-600">Order ID:</span>
                <span className="ml-2 font-medium text-gray-800">#{order.orderId}</span>
              </div>
            </div>

            <div className="flex items-center text-sm">
              <Calendar size={18} className="mr-3 text-gray-400" />
              <div>
                <span className="text-gray-600">Order Date:</span>
                <span className="ml-2 font-medium text-gray-800">
                  {formatDateTime(order.orderDate)}
                </span>
              </div>
            </div>

            {(order.deliveryStatus === "BOOK_WITH_ME" || order.deliveredDate) && (
              <div className="flex items-center text-sm">
                <CheckCircle size={18} className="mr-3 text-green-500" />
                <div>
                  <span className="text-gray-600">Delivered Date:</span>
                  <span className="ml-2 font-medium text-green-700">
                    {formatDateTime(order.deliveredDate || order.orderDate)}
                  </span>
                </div>
              </div>
            )}

            {order.returnDate && (
              <div className="flex items-center text-sm">
                <Calendar size={18} className="mr-3 text-gray-400" />
                <div>
                  <span className="text-gray-600">Return Date:</span>
                  <span className="ml-2 font-medium text-gray-800">
                    {formatDateTime(order.returnDate)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Customer Info */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 space-y-3 border border-green-100">
            <h4 className="font-bold text-gray-900 mb-3 flex items-center">
              <User size={18} className="mr-2 text-green-600" />
              Customer Information
            </h4>
            
            <div className="flex items-center text-sm">
              <User size={18} className="mr-3 text-gray-400" />
              <div>
                <span className="text-gray-600">Name:</span>
                <span className="ml-2 font-medium text-gray-800">{order.customerName}</span>
              </div>
            </div>

            <div className="flex items-center text-sm">
              <Phone size={18} className="mr-3 text-gray-400" />
              <div className="flex-1 flex items-center justify-between">
                <div>
                  <span className="text-gray-600">Phone:</span>
                  <span className="ml-2 font-medium text-gray-800">{order.customerPhone}</span>
                </div>
                <button
                  onClick={handleCallCustomer}
                  className="ml-4 px-3 py-1 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition text-sm"
                >
                  Call
                </button>
              </div>
            </div>

            {order.customerEmail && (
              <div className="flex items-center text-sm">
                <Mail size={18} className="mr-3 text-gray-400" />
                <div>
                  <span className="text-gray-600">Email:</span>
                  <span className="ml-2 font-medium text-gray-800">{order.customerEmail}</span>
                </div>
              </div>
            )}
          </div>

          {/* Delivery Address */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-5 space-y-3 border border-purple-100">
            <h4 className="font-bold text-gray-900 mb-3 flex items-center">
              <MapPin size={18} className="mr-2 text-purple-600" />
              Delivery Address
            </h4>
            
            <div className="flex items-start text-sm">
              <MapPin size={18} className="mr-3 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-gray-800 font-medium mb-1">{order.deliveryAddress}</p>
                <p className="text-gray-600">
                  {order.city} - {order.pincode}
                </p>
              </div>
            </div>

            <button
              onClick={handleOpenMaps}
              className="w-full mt-3 flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition shadow-md hover:shadow-lg font-medium"
            >
              <MapPin size={18} />
              <span>Open in Google Maps</span>
            </button>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gradient-to-r from-gray-50 to-white border-t px-6 py-5 flex gap-3 rounded-b-2xl">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition font-medium"
          >
            Close
          </button>
          
          {canUpdate && nextStatus && (
            <button
              onClick={handleStatusUpdate}
              disabled={updating}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-primary to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 transition shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {updating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <span>Mark as {getStatusLabel(nextStatus)}</span>
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default DeliveryOrderDetails;
